import { APIProvider, Map, Marker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useState, useRef, useMemo } from "react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function LiveTrackingMap({ bookingId, driverLocation, departure, destination, departureCoords, destinationCoords, height = "350px" }) {
  if (!MAPS_KEY) {
    return (
      <div className="rounded-lg bg-[#F8FAFC] border border-[#CBD5E1] flex items-center justify-center" style={{ height }}>
        <p className="text-xs text-[#64748B]">Carte non disponible</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden border border-[#CBD5E1] relative" style={{ height }} data-testid="live-tracking-map">
      {driverLocation && (
        <div className="absolute top-3 left-3 z-10 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-[#CBD5E1] shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
            <span className="text-xs font-semibold text-[#0A2540]">Chauffeur en direct</span>
          </div>
        </div>
      )}
      <APIProvider apiKey={MAPS_KEY}>
        <TrackingMapInner
          driverLocation={driverLocation}
          departureCoords={departureCoords}
          destinationCoords={destinationCoords}
          departure={departure}
          destination={destination}
        />
      </APIProvider>
    </div>
  );
}

function TrackingMapInner({ driverLocation, departureCoords, destinationCoords, departure, destination }) {
  const map = useMap();
  const routesLib = useMapsLibrary("routes");
  const rendererRef = useRef(null);

  // Smoothly interpolated driver position (state) + heading (rotation, deg).
  const [displayPos, setDisplayPos] = useState(driverLocation?.lat ? driverLocation : null);
  const [heading, setHeading] = useState(0);
  const animRef = useRef({ raf: null, from: null, to: null, start: 0, dur: 900 });

  // Whenever driverLocation changes, animate the marker from the current
  // display position to the new one over ~900ms.
  useEffect(() => {
    if (!driverLocation?.lat || !driverLocation?.lng) return;
    const to = { lat: driverLocation.lat, lng: driverLocation.lng };
    const from = displayPos && displayPos.lat ? { lat: displayPos.lat, lng: displayPos.lng } : to;

    // Compute heading (bearing) in degrees for icon rotation.
    if (from.lat !== to.lat || from.lng !== to.lng) {
      const φ1 = (from.lat * Math.PI) / 180;
      const φ2 = (to.lat * Math.PI) / 180;
      const Δλ = ((to.lng - from.lng) * Math.PI) / 180;
      const y = Math.sin(Δλ) * Math.cos(φ2);
      const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
      const brng = (Math.atan2(y, x) * 180) / Math.PI;
      setHeading((brng + 360) % 360);
    }

    // Cancel prior animation, start a new one.
    if (animRef.current.raf) cancelAnimationFrame(animRef.current.raf);
    animRef.current = { raf: null, from, to, start: performance.now(), dur: 900 };

    const step = (now) => {
      const { start, dur, from: f, to: t } = animRef.current;
      const p = Math.min(1, (now - start) / dur);
      // ease-out cubic for a natural glide
      const e = 1 - Math.pow(1 - p, 3);
      const lat = f.lat + (t.lat - f.lat) * e;
      const lng = f.lng + (t.lng - f.lng) * e;
      setDisplayPos({ lat, lng });
      if (p < 1) {
        animRef.current.raf = requestAnimationFrame(step);
      } else {
        animRef.current.raf = null;
      }
    };
    animRef.current.raf = requestAnimationFrame(step);

    return () => {
      if (animRef.current.raf) {
        cancelAnimationFrame(animRef.current.raf);
        animRef.current.raf = null;
      }
    };
  }, [driverLocation?.lat, driverLocation?.lng]);

  const departureIcon = useMemo(() => ({
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="%2310B981"><circle cx="12" cy="12" r="8" fill="%2310B981" stroke="white" stroke-width="3"/></svg>'),
    scaledSize: { width: 24, height: 24 },
  }), []);

  const destinationIcon = useMemo(() => ({
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="%23EF4444"><circle cx="12" cy="12" r="8" fill="%23EF4444" stroke="white" stroke-width="3"/></svg>'),
    scaledSize: { width: 24, height: 24 },
  }), []);

  const driverIcon = useMemo(() => {
    // Yellow car (top-down view) SVG — rotates via `heading` if provided
    // by parent (rotation is applied through the marker position update).
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42">
      <g transform="rotate(${heading} 21 21)">
        <circle cx="21" cy="21" r="20" fill="rgba(250,204,21,0.18)"/>
        <circle cx="21" cy="21" r="14" fill="rgba(250,204,21,0.35)"/>
        <g transform="translate(21 21)">
          <path d="M -6 -9 L 6 -9 L 8 -3 L 8 8 L 6 10 L -6 10 L -8 8 L -8 -3 Z"
                fill="#FACC15" stroke="#0A2540" stroke-width="1.4" stroke-linejoin="round"/>
          <rect x="-5" y="-7.5" width="10" height="5.5" rx="1.2" fill="#0A2540" opacity="0.85"/>
          <rect x="-5" y="0.5" width="10" height="4" rx="1" fill="#0A2540" opacity="0.55"/>
          <circle cx="-5.5" cy="-4.5" r="0.9" fill="#FFFFFF"/>
          <circle cx="5.5"  cy="-4.5" r="0.9" fill="#FFFFFF"/>
        </g>
      </g>
    </svg>`;
    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      scaledSize: { width: 42, height: 42 },
      anchor: { x: 21, y: 21 },
    };
  }, [heading]);

  useEffect(() => {
    if (!map || !routesLib || !departureCoords || !destinationCoords) return;

    if (!rendererRef.current) {
      const directionsService = new routesLib.DirectionsService();
      rendererRef.current = new routesLib.DirectionsRenderer({
        map,
        suppressMarkers: true,
        polylineOptions: { strokeColor: "#0A2540", strokeWeight: 4, strokeOpacity: 0.6 },
      });

      directionsService.route(
        {
          origin: { lat: departureCoords.lat, lng: departureCoords.lng },
          destination: { lat: destinationCoords.lat, lng: destinationCoords.lng },
          travelMode: routesLib.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === "OK" && rendererRef.current) {
            rendererRef.current.setDirections(result);
          }
        }
      );
    }

    return () => {
      if (rendererRef.current) {
        rendererRef.current.setMap(null);
        rendererRef.current = null;
      }
    };
  }, [map, routesLib, departureCoords, destinationCoords]);

  // Center on driver when the smoothly-interpolated position updates.
  useEffect(() => {
    if (map && displayPos?.lat && displayPos?.lng) {
      map.panTo({ lat: displayPos.lat, lng: displayPos.lng });
    }
  }, [map, displayPos?.lat, displayPos?.lng]);

  const center = displayPos?.lat ? { lat: displayPos.lat, lng: displayPos.lng } : departureCoords || { lat: 3.848, lng: 11.5021 };

  return (
    <Map
      defaultCenter={center}
      defaultZoom={destinationCoords ? 8 : 12}
      mapId="trigotour-tracking"
      style={{ width: "100%", height: "100%" }}
      gestureHandling="greedy"
      disableDefaultUI={false}
      zoomControl={true}
      mapTypeControl={false}
      streetViewControl={false}
    >
      {/* Departure marker */}
      {departureCoords && (
        <Marker
          position={departureCoords}
          title={departure || "Depart"}
          icon={departureIcon}
        />
      )}

      {/* Destination marker */}
      {destinationCoords && (
        <Marker
          position={destinationCoords}
          title={destination || "Destination"}
          icon={destinationIcon}
        />
      )}

      {/* Driver live marker (smoothly interpolated) */}
      {displayPos?.lat && displayPos?.lng && (
        <Marker
          position={displayPos}
          title="Chauffeur"
          icon={driverIcon}
        />
      )}
    </Map>
  );
}
